/* eslint-disable max-lines-per-function, complexity, max-lines */
/* Cohesive single-file section component — see project guidelines. */
import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, Send, Instagram, Youtube } from "lucide-react";
import { submitContact, subscribeNewsletter } from "@/lib/api";
import { CONTACT } from "@/data/site";

export const Contact = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [news, setNews] = useState("");
    const [newsSubmitting, setNewsSubmitting] = useState(false);

    const onChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill out name, email, and message.");
            return;
        }
        setSubmitting(true);
        try {
            await submitContact(form);
            toast.success("Message received. We'll be in touch.");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            toast.error("Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const subscribe = async (e) => {
        e.preventDefault();
        if (!news) return;
        setNewsSubmitting(true);
        try {
            await subscribeNewsletter(news);
            toast.success("You're on the list. Welcome to the movement.");
            setNews("");
        } catch (err) {
            toast.error("Subscribe failed. Please try again.");
        } finally {
            setNewsSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-12 md:py-16 bg-[#0A0A0A]"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-12">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        className="lg:col-span-5"
                    >
                        <span className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                            / 11 — Connect
                        </span>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
                            Let's
                            <br />
                            <span className="gold-text-gradient">
                                Build Together.
                            </span>
                        </h2>
                        <p className="mt-6 text-white/65 leading-relaxed max-w-md">
                            Whether you're a student, a parent, a school
                            district, or an industry professional — drop us a
                            line and we'll get back to you.
                        </p>

                        <div className="mt-10 space-y-4">
                            <a
                                href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
                                data-testid="contact-phone"
                                className="flex items-center gap-4 group"
                            >
                                <div className="h-12 w-12 border border-[#D4AF37]/30 grid place-items-center group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-all">
                                    <Phone
                                        size={18}
                                        className="text-[#D4AF37] group-hover:text-black"
                                    />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                                        Phone
                                    </div>
                                    <div className="font-display text-xl tracking-wide text-white group-hover:text-[#D4AF37] transition-colors">
                                        {CONTACT.phone}
                                    </div>
                                </div>
                            </a>
                            <a
                                href={`mailto:${CONTACT.email}`}
                                data-testid="contact-email"
                                className="flex items-center gap-4 group"
                            >
                                <div className="h-12 w-12 border border-[#D4AF37]/30 grid place-items-center group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-all">
                                    <Mail
                                        size={18}
                                        className="text-[#D4AF37] group-hover:text-black"
                                    />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                                        Email
                                    </div>
                                    <div className="font-display text-lg tracking-wide text-white group-hover:text-[#D4AF37] transition-colors break-all">
                                        {CONTACT.email}
                                    </div>
                                </div>
                            </a>
                            <a
                                href="https://www.instagram.com/edutainmentpros"
                                target="_blank"
                                rel="noreferrer noopener"
                                data-testid="contact-instagram"
                                className="flex items-center gap-4 group"
                            >
                                <div className="h-12 w-12 border border-[#D4AF37]/30 grid place-items-center group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-all">
                                    <Instagram
                                        size={18}
                                        className="text-[#D4AF37] group-hover:text-black"
                                    />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                                        Instagram
                                    </div>
                                    <div className="font-display text-lg tracking-wide text-white group-hover:text-[#D4AF37] transition-colors">
                                        @edutainmentpros
                                    </div>
                                </div>
                            </a>
                            <a
                                href="https://www.youtube.com/@edutainmentpros4123"
                                target="_blank"
                                rel="noreferrer noopener"
                                data-testid="contact-youtube"
                                className="flex items-center gap-4 group"
                            >
                                <div className="h-12 w-12 border border-[#D4AF37]/30 grid place-items-center group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-all">
                                    <Youtube
                                        size={18}
                                        className="text-[#D4AF37] group-hover:text-black"
                                    />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                                        YouTube
                                    </div>
                                    <div className="font-display text-lg tracking-wide text-white group-hover:text-[#D4AF37] transition-colors">
                                        @edutainmentpros4123
                                    </div>
                                </div>
                            </a>
                        </div>

                        {/* Newsletter */}
                        <form
                            onSubmit={subscribe}
                            data-testid="newsletter-form"
                            className="mt-10 border border-white/10 p-5 glass-dark"
                        >
                            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
                                Join The Newsletter
                            </div>
                            <div className="flex gap-0">
                                <input
                                    type="email"
                                    required
                                    data-testid="newsletter-email"
                                    value={news}
                                    onChange={(e) => setNews(e.target.value)}
                                    placeholder="you@example.com"
                                    className="flex-1 bg-black/40 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none"
                                />
                                <button
                                    data-testid="newsletter-submit"
                                    disabled={newsSubmitting}
                                    className="bg-[#D4AF37] text-black px-5 font-display tracking-widest text-xs uppercase hover:bg-[#FFF3A1] transition disabled:opacity-50"
                                >
                                    Join
                                </button>
                            </div>
                        </form>
                    </motion.div>

                    {/* Contact form */}
                    <motion.form
                        onSubmit={submit}
                        data-testid="contact-form"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        className="lg:col-span-7 glass-gold p-8 md:p-12"
                    >
                        <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight text-white">
                            Send A Message
                        </h3>
                        <div className="h-px w-12 bg-[#D4AF37] mt-3 mb-8" />

                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field
                                label="Full Name"
                                name="name"
                                value={form.name}
                                onChange={onChange}
                                placeholder="Your name"
                                testId="contact-name"
                                required
                            />
                            <Field
                                label="Email"
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={onChange}
                                placeholder="you@example.com"
                                testId="contact-email-input"
                                required
                            />
                        </div>
                        <Field
                            label="Subject"
                            name="subject"
                            value={form.subject}
                            onChange={onChange}
                            placeholder="What's this about?"
                            testId="contact-subject"
                        />
                        <div className="mt-4">
                            <label className="text-[11px] uppercase tracking-[0.3em] text-white/50">
                                Message
                            </label>
                            <textarea
                                name="message"
                                value={form.message}
                                onChange={onChange}
                                rows={6}
                                required
                                data-testid="contact-message"
                                placeholder="Tell us what you're thinking..."
                                className="mt-2 w-full bg-black/40 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none resize-none"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit"
                            className="mt-6 inline-flex items-center gap-3 bg-[#D4AF37] text-black font-display uppercase tracking-widest px-8 py-4 hover:bg-[#FFF3A1] transition-all hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] disabled:opacity-50"
                        >
                            {submitting ? "Sending..." : "Send Message"}
                            <Send size={16} />
                        </button>
                    </motion.form>
                </div>
            </div>
        </section>
    );
};

const Field = ({ label, testId, required, ...rest }) => (
    <div>
        <label className="text-[11px] uppercase tracking-[0.3em] text-white/50">
            {label}
            {required && <span className="text-[#D4AF37] ml-1">*</span>}
        </label>
        <input
            {...rest}
            data-testid={testId}
            required={required}
            className="mt-2 w-full bg-black/40 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none"
        />
    </div>
);
